import { record } from 'rrweb';
import type { eventWithTime } from '@rrweb/types';
import type { DomEvent } from '@logclip/types';
import type { InstrumentCtx, Teardown } from './context.js';
import { uuid } from './util.js';
import { redactUrl } from './mask.js';

// rrweb EventType.IncrementalSnapshot === 3 (a real DOM mutation/interaction).
const INCREMENTAL = 3;
// rrweb EventType.Meta === 4 ({ href, width, height }).
const META = 4;

export function instrumentDom(ctx: InstrumentCtx): Teardown {
  const stop = record({
    emit(event: eventWithTime) {
      const t = ctx.clock.fromPerf(performanceNow());
      // ONE CLOCK: rrweb stamps events with Date.now(), while everything else in
      // the session runs on the monotonic perf clock (`t`). Rewrite the rrweb
      // timestamp onto that same clock (startedAt + t) so an NTP step / manual
      // clock change mid-session can't desync replay from the console/network
      // lanes — the player only ever uses timestamp DELTAS, so this is safe.
      event.timestamp = ctx.clock.startedAt + t;
      // rrweb's Meta event ({href, width, height}) — at session start and every
      // 30 s checkpoint — carries the page's FULL URL. Redact it like every other
      // URL we send (entryUrl, pageviews, network): query strings hold utm tags,
      // ad click ids and reset/verify `?token=`s. Nothing reads `href` on replay
      // (the replayer uses width/height; snapshots already hold absolute URLs).
      if (event.type === META) {
        const data = (event as { data?: { href?: unknown } }).data;
        if (data && typeof data.href === 'string') data.href = redactUrl(data.href);
      }
      const dom: DomEvent = {
        id: uuid(),
        sessionId: ctx.sessionId,
        type: 'dom',
        t,
        ts: ctx.clock.wall(),
        data: event,
      };
      ctx.emit(dom);
      if (event.type === INCREMENTAL) ctx.markActivity();
    },
    maskAllInputs: ctx.config.maskAllInputs,
    // Password inputs stay masked even in no-privacy mode (maskAllInputs:false) —
    // a hard floor so recordings never store plaintext passwords.
    maskInputOptions: { password: true },
    maskTextSelector: ctx.config.maskTextSelector,
    blockSelector: ctx.config.blockSelector,
    recordCanvas: ctx.config.recordCanvas,
    // Periodic full snapshots → the player can start replay from the nearest
    // checkpoint, enabling progressive buffering + reliable seeking (§8.6).
    checkoutEveryNms: 30_000,
    sampling: {
      // Don't record every mousemove point; sample for a smaller payload.
      mousemove: 50,
      scroll: 150,
      input: 'last',
    },
  });

  return () => {
    try {
      stop?.();
    } catch {
      /* already stopped */
    }
  };
}

/** Force a fresh FullSnapshot checkpoint NOW. Used after transport backpressure
 *  had to drop buffered DOM deltas — the new snapshot re-anchors the stream so
 *  replay stays coherent instead of playing broken deltas until the next 30s
 *  checkout. Safe no-op when recording isn't active. */
export function takeFullSnapshot(): void {
  try {
    record.takeFullSnapshot(true);
  } catch {
    /* not recording */
  }
}

function performanceNow(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}
